import {announce} from '@github-ui/aria-live'
import {CheckCircleIcon} from '@primer/octicons-react'
import {useEffect, useEffectEvent, useRef} from 'react'

import styles from './AddSubIssueButtonGroup.module.css'
import {type SubIssueFeedback, useSubIssueFeedback} from './SubIssueStateContext'

/**
 * Confirms a sub-issue change beside the buttons that made it, so people using magnification see it
 * where they are. A created sub-issue's confirmation takes focus so screen readers read it, and is kept
 * out of the tab order. It isn't a live region, and only announces when it can't take focus, because
 * both would read it twice.
 */
export function SubIssueFeedbackMessage({feedback}: {feedback: SubIssueFeedback}) {
  const ref = useRef<HTMLSpanElement>(null)
  const {canFocusFeedback} = useSubIssueFeedback()

  const confirm = useEffectEvent((message: string) => {
    if (!feedback.takeFocus) return
    if (canFocusFeedback()) {
      ref.current?.focus()
    } else {
      announce(message)
    }
  })

  useEffect(() => confirm(feedback.message), [feedback.id, feedback.message])

  return (
    <span ref={ref} tabIndex={-1} className={styles.feedback} data-testid="sub-issue-feedback">
      <CheckCircleIcon className={styles.feedbackIcon} />
      {feedback.message}
    </span>
  )
}
